<template>
  <div>
    <div>
      <div class="my-3">
        <h1 class="h1 text-grey-light-900 font-semibold text-3xl mb-3">Sign Up</h1>
        <p class="text-sm font-normal text-black">Please fill in your contact details.</p>
        <ErrorDisplay v-if="errorMessage" :error-message="errorMessage" />
      </div>
    </div>

    <form id="signUpForm">
      <UsernameInput
        v-model:username="form.cellNumber"
        v-model:is-username-valid="isUsernameValid"
        type="MOBILE"
        testId="mobile"
        label="Mobile Number"
        class="cellnumber text-left"
        :disabled="isLoading"
        @error="
          (error: string) => {
            errorMessage = error;
          }
        "
      />

      <PasswordInput v-model="form.password" :disabled="isLoading" :label="`Password`" class="password text-left" />

      <PasswordInput
        v-model="confirmPassword"
        :disabled="isLoading"
        :label="`Confirm Password`"
        class="confirm-password text-left"
      />
      <TurnstileWidget @success="handleSuccess" />
      <LayupButton
        class="bg-brand-600 rounded w-100 relative h-10 text-base-white hover:bg-brand-700 disabled:opacity-50 disabled:bg-brand-600"
        type="submit"
        :disabled="isLoading || !form.cellNumber || !form.password || !confirmPassword || !turnstileToken"
        data-testid="sign_up_button"
        :label="'Sign up'"
        @click="onSubmit()"
        :isLoading="isLoading"
      />

      <button
        type="button"
        class="w-full border rounded h-10 w-100 mt-3 disabled:opacity-50 hover:bg-gray-500 hover:text-white text-black"
        @click="goBack()"
        :disabled="isLoading"
      >
        Cancel
      </button>

      <div class="text-center mt-3 text-sm text-black">
        <p>
          Already have an account?
          <RouterLink to="sign-in" class="btn text-brand-700 p-0 text-sm">Sign in</RouterLink>
        </p>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import ErrorDisplay from '@/components/common/ErrorDisplay.vue';
import LayupButton from '@/components/common/LayupButton.vue';
import PasswordInput from '@/components/common/PasswordInput.vue';
import TurnstileWidget from '@/components/common/TurnstileWidget.vue';
import UsernameInput from '@/components/common/UsernameInput.vue';
import { NetworkError } from '@/errors';
import { getErrorMessage, signUp } from '@/services/auth';
import { useUserStore } from '@/stores/users';
import { isValidPassword, parseMobile } from '@/utils/form.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  channel: string;
}>();

const userStore = useUserStore();
const router = useRouter();
const turnstileToken = ref('');
const errorMessage = ref('');
const isLoading = ref(false);

const form = {
  cellNumber: '',
  password: '',
};
const isUsernameValid = ref(false);
const confirmPassword = ref('');

const onSubmit = async () => {
  try {
    errorMessage.value = '';
    const passwordValidation = isValidPassword(form.password, confirmPassword.value);
    if (!passwordValidation.isValid) {
      errorMessage.value = passwordValidation.errorMessage;
    } else {
      isLoading.value = true;
      form.cellNumber = parseMobile(form.cellNumber);
      const response = await signUp(form.cellNumber, form.password, turnstileToken.value);
      addMetric('sign-up', { status: 'success', channel: props.channel });
      userStore.user.cellNumber = response.cellNumber;
      userStore.user._id = response._id;
      userStore.user.name = response.name;
      if (response.cellNumber) {
        await router.push({ path: 'otp-verification' });
      }
    }
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('sign-up', { status: 'network_error', channel: props.channel });
      errorMessage.value = 'Network Error. Please try again.';
    } else {
      createException(error);
      addMetric('sign-up', { status: 'error', channel: props.channel });
      errorMessage.value = getErrorMessage(error);
    }
    isLoading.value = false;
  }
};

const goBack = () => {
  router.back();
};
function handleSuccess(token: string) {
  turnstileToken.value = token;
}
onUnmounted(() => {
  isLoading.value = false;
});
</script>
